import { alphabeticalCompare } from '@cims-online/shared'
import type {
  RecordsTableFieldColumn,
  RecordsTableFieldProperty,
  RecordsTableStatusColumn,
} from '@cims-online/shared'
import type { ColumnType } from 'antd/lib/table'
import type { WorkflowFieldValue, WorkflowRecord } from '../../types'
import { findFieldValue, statusText } from './fieldValues'
import { fieldValueText } from './renderFieldValue'

type RecordsColumnSort = Pick<
  ColumnType<WorkflowRecord>,
  'defaultSortOrder' | 'sorter'
>

function sortOptions(
  compare: (a: WorkflowRecord, b: WorkflowRecord) => number,
  defaultSortOrder: RecordsColumnSort['defaultSortOrder'],
  priority: number | undefined
): RecordsColumnSort {
  return {
    defaultSortOrder,
    sorter: {
      compare,
      multiple:
        priority === undefined ? undefined : 1 / (Math.abs(priority) + 1),
    },
  }
}

function numberValue(
  fieldValue: WorkflowFieldValue | undefined,
  property: RecordsTableFieldProperty
) {
  if (property !== 'value' || fieldValue?.type !== 'generated_int') return
  return fieldValue.value
}

/**
 * Converts a serialized field sort into native Ant Design sort options.
 *
 * Numeric sorts compare the configured underlying number instead of its
 * formatted display text. Alphabetical sorts use the normalized text users see
 * in the column.
 */
export function fieldSort(
  column: RecordsTableFieldColumn
): RecordsColumnSort | undefined {
  if (!column.sort) return

  const { default: defaultDirection, priority, type, value } = column.sort
  if (type === 'number')
    return sortOptions(
      (a, b) =>
        alphabeticalCompare(
          numberValue(findFieldValue(a, column.fieldId)?.value, value),
          numberValue(findFieldValue(b, column.fieldId)?.value, value)
        ),
      defaultDirection,
      priority
    )

  return sortOptions(
    (a, b) =>
      alphabeticalCompare(
        fieldValueText(findFieldValue(a, column.fieldId)?.value, value),
        fieldValueText(findFieldValue(b, column.fieldId)?.value, value)
      ),
    defaultDirection,
    priority
  )
}

/**
 * Applies the server-selected status strategy.
 *
 * Specified sorting follows the configured rendered status order. Values absent
 * from that order follow configured values alphabetically.
 */
export function statusSort(
  column: RecordsTableStatusColumn
): RecordsColumnSort {
  const { sort } = column
  if (sort.type === 'alphabetical')
    return sortOptions(
      (a, b) => alphabeticalCompare(statusText(a), statusText(b)),
      sort.default,
      sort.priority
    )

  const ranks = new Map(
    sort.order.map((status, index) => [status, index] as const)
  )
  return sortOptions(
    (a, b) => {
      const aStatus = statusText(a)
      const bStatus = statusText(b)
      const fallbackRank = sort.order.length
      const rankDifference =
        (ranks.get(aStatus) ?? fallbackRank) -
        (ranks.get(bStatus) ?? fallbackRank)
      if (rankDifference) return rankDifference
      return alphabeticalCompare(aStatus, bStatus)
    },
    sort.default,
    sort.priority
  )
}
